import React, { useEffect, useState } from 'react';
import { SearchIcon, FilterIcon, UserCheckIcon, UserXIcon, EyeIcon, SettingsIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, SaveIcon, AlertCircleIcon, BuildingIcon, ShieldIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import StatusBadge from '../../components/ui/StatusBadge';
// Mock merchant data
const merchantsData = [{
  id: 'M001',
  name: 'Global Electronics',
  businessType: 'Electronics',
  tier: 'Premium',
  status: 'active',
  portalAccess: true,
  lastLogin: '2023-10-28T10:30:00Z',
  features: {
    transactions: true,
    qrGeneration: true,
    reports: true,
    profileManagement: true,
    campaigns: true,
    multiMerchant: true
  },
  roles: [{
    id: 'admin',
    name: 'Admin',
    count: 2
  }, {
    id: 'manager',
    name: 'Manager',
    count: 3
  }, {
    id: 'staff',
    name: 'Staff',
    count: 5
  }]
}, {
  id: 'M002',
  name: 'City Retailers',
  businessType: 'Retail',
  tier: 'Premium',
  status: 'active',
  portalAccess: true,
  lastLogin: '2023-10-27T14:45:00Z',
  features: {
    transactions: true,
    qrGeneration: true,
    reports: true,
    profileManagement: true,
    campaigns: false,
    multiMerchant: false
  },
  roles: [{
    id: 'admin',
    name: 'Admin',
    count: 1
  }, {
    id: 'manager',
    name: 'Manager',
    count: 2
  }]
}, {
  id: 'M003',
  name: 'Gourmet Foods',
  businessType: 'Food & Beverage',
  tier: 'Standard',
  status: 'active',
  portalAccess: false,
  lastLogin: null,
  features: {
    transactions: false,
    qrGeneration: false,
    reports: false,
    profileManagement: false,
    campaigns: false,
    multiMerchant: false
  },
  roles: []
}, {
  id: 'M004',
  name: 'Tech Solutions Inc',
  businessType: 'Technology',
  tier: 'Premium',
  status: 'active',
  portalAccess: true,
  lastLogin: '2023-10-25T09:15:00Z',
  features: {
    transactions: true,
    qrGeneration: true,
    reports: true,
    profileManagement: true,
    campaigns: true,
    multiMerchant: false
  },
  roles: [{
    id: 'admin',
    name: 'Admin',
    count: 1
  }, {
    id: 'manager',
    name: 'Manager',
    count: 4
  }, {
    id: 'staff',
    name: 'Staff',
    count: 8
  }]
}, {
  id: 'M005',
  name: 'Fashion Forward',
  businessType: 'Apparel',
  tier: 'Standard',
  status: 'inactive',
  portalAccess: false,
  lastLogin: null,
  features: {
    transactions: false,
    qrGeneration: false,
    reports: false,
    profileManagement: false,
    campaigns: false,
    multiMerchant: false
  },
  roles: []
}, {
  id: 'M006',
  name: 'Metro Hospitality',
  businessType: 'Hospitality',
  tier: 'Premium',
  status: 'active',
  portalAccess: true,
  lastLogin: '2023-10-26T11:30:00Z',
  features: {
    transactions: true,
    qrGeneration: true,
    reports: true,
    profileManagement: true,
    campaigns: false,
    multiMerchant: true
  },
  roles: [{
    id: 'admin',
    name: 'Admin',
    count: 2
  }, {
    id: 'manager',
    name: 'Manager',
    count: 5
  }]
}];
// Define feature descriptions
const featureDescriptions = {
  transactions: 'View and manage transaction history',
  qrGeneration: 'Generate and manage QR codes for payments',
  reports: 'Access detailed reports and analytics',
  profileManagement: 'Update merchant profile and settings',
  campaigns: 'View and participate in marketing campaigns',
  multiMerchant: 'Manage multiple merchant accounts'
};
// Define role permissions
const rolePermissions = {
  admin: {
    name: 'Admin',
    description: 'Full access to all features',
    permissions: ['View transactions', 'Generate QR codes', 'View reports', 'Manage profile', 'View campaigns', 'Manage users', 'Configure settings']
  },
  manager: {
    name: 'Manager',
    description: 'Access to most features with some restrictions',
    permissions: ['View transactions', 'Generate QR codes', 'View reports', 'View profile', 'View campaigns']
  },
  staff: {
    name: 'Staff',
    description: 'Limited access to basic features',
    permissions: ['View transactions', 'Generate QR codes']
  }
};
interface Merchant {
  id: string;
  name: string;
  businessType: string;
  tier: string;
  status: string;
  portalAccess: boolean;
  lastLogin: string | null;
  features: {
    transactions: boolean;
    qrGeneration: boolean;
    reports: boolean;
    profileManagement: boolean;
    campaigns: boolean;
    multiMerchant: boolean;
  };
  roles: Array<{
    id: string;
    name: string;
    count: number;
  }>;
}
const MerchantAccessControl: React.FC = () => {
  const [merchants, setMerchants] = useState<Merchant[]>(merchantsData);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>(merchantsData);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [tierFilter, setTierFilter] = useState('');
  const [accessFilter, setAccessFilter] = useState('');
  // Modal states
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Edit states
  const [editedFeatures, setEditedFeatures] = useState<Record<string, boolean>>({});
  const [bulkEnableDisable, setBulkEnableDisable] = useState<'enable' | 'disable' | ''>('');
  // Apply filters
  const applyFilters = () => {
    let result = merchants;
    // Search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(merchant => merchant.name.toLowerCase().includes(term) || merchant.id.toLowerCase().includes(term) || merchant.businessType.toLowerCase().includes(term));
    }
    // Tier filter
    if (tierFilter) {
      result = result.filter(merchant => merchant.tier === tierFilter);
    }
    // Access filter
    if (accessFilter === 'enabled') {
      result = result.filter(merchant => merchant.portalAccess);
    } else if (accessFilter === 'disabled') {
      result = result.filter(merchant => !merchant.portalAccess);
    }
    setFilteredMerchants(result);
  };
  // Reset filters
  const resetFilters = () => {
    setTierFilter('');
    setAccessFilter('');
    setSearchTerm('');
  };
  // Toggle portal access
  const togglePortalAccess = (merchant: Merchant) => {
    if (merchant.portalAccess) {
      // If disabling access, show confirmation
      setSelectedMerchant(merchant);
      setShowConfirmModal(true);
    } else {
      // If enabling access, do it directly
      updateMerchantAccess(merchant, true);
    }
  };
  // Update merchant access
  const updateMerchantAccess = (merchant: Merchant, access: boolean) => {
    const updatedMerchants = merchants.map(m => {
      if (m.id === merchant.id) {
        return {
          ...m,
          portalAccess: access,
          // If enabling access, set default features
          features: access ? {
            transactions: true,
            qrGeneration: true,
            reports: true,
            profileManagement: true,
            campaigns: false,
            multiMerchant: false
          } : {
            transactions: false,
            qrGeneration: false,
            reports: false,
            profileManagement: false,
            campaigns: false,
            multiMerchant: false
          },
          // If enabling access, add default admin role
          roles: access ? [{
            id: 'admin',
            name: 'Admin',
            count: 1
          }] : []
        };
      }
      return m;
    });
    setMerchants(updatedMerchants);
    applyFilters();
    setShowConfirmModal(false);
  };
  // Open features modal
  const openFeaturesModal = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setEditedFeatures(merchant.features);
    setShowFeaturesModal(true);
  };
  // Open roles modal
  const openRolesModal = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setShowRolesModal(true);
  };
  // Save features
  const saveFeatures = () => {
    if (!selectedMerchant) return;
    const updatedMerchants = merchants.map(m => {
      if (m.id === selectedMerchant.id) {
        return {
          ...m,
          features: editedFeatures as typeof m.features
        };
      }
      return m;
    });
    setMerchants(updatedMerchants);
    applyFilters();
    setShowFeaturesModal(false);
  };
  // Handle feature change
  const handleFeatureChange = (feature: string, value: boolean) => {
    setEditedFeatures(prev => ({
      ...prev,
      [feature]: value
    }));
  };
  // Bulk enable/disable features
  const handleBulkFeatureChange = () => {
    if (!bulkEnableDisable) return;
    const allEnabled = bulkEnableDisable === 'enable';
    setEditedFeatures({
      transactions: allEnabled,
      qrGeneration: allEnabled,
      reports: allEnabled,
      profileManagement: allEnabled,
      campaigns: allEnabled,
      multiMerchant: allEnabled
    });
    setBulkEnableDisable('');
  };
  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Effect to apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [searchTerm, tierFilter, accessFilter, merchants]);
  return <div className="space-y-6" data-id="element-632">
      <div data-id="element-633">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-634">
          Merchant Portal Access Control
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-635">
          Manage merchant access to the self-service portal
        </p>
      </div>
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4" data-id="element-636">
        <div className="relative flex-1" data-id="element-637">
          <input type="text" placeholder="Search merchants..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-638" />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-639">
            <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-640" />
          </div>
        </div>
        <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-641">
          <FilterIcon className="h-4 w-4 mr-2" data-id="element-642" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>
      {/* Advanced Filters */}
      {showFilters && <Card data-id="element-643">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-644">
            <div data-id="element-645">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-646">
                Merchant Tier
              </label>
              <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-647">
                <option value="" data-id="element-648">All Tiers</option>
                <option value="Premium" data-id="element-649">Premium</option>
                <option value="Standard" data-id="element-650">Standard</option>
                <option value="Basic" data-id="element-651">Basic</option>
              </select>
            </div>
            <div data-id="element-652">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-653">
                Portal Access
              </label>
              <select value={accessFilter} onChange={e => setAccessFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-654">
                <option value="" data-id="element-655">All</option>
                <option value="enabled" data-id="element-656">Enabled</option>
                <option value="disabled" data-id="element-657">Disabled</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-658">
            <button onClick={resetFilters} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-659">
              Reset
            </button>
            <button onClick={() => setShowFilters(false)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-660">
              Apply Filters
            </button>
          </div>
        </Card>}
      {/* Merchants Table */}
      <Card data-id="element-661">
        <div className="mb-4 flex items-center justify-between" data-id="element-662">
          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-663">
            Showing {filteredMerchants.length} merchants
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-664">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-665">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-666">
              <tr data-id="element-667">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-668">
                  Merchant
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-669">
                  Tier
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-670">
                  Portal Access
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-671">
                  Last Login
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-672">
                  Features
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-673">
                  Users
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-674">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-675">
              {filteredMerchants.map(merchant => <tr key={merchant.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-676">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-677">
                    <div className="flex items-center" data-id="element-678">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30" data-id="element-679">
                        <BuildingIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-680" />
                      </div>
                      <div className="ml-4" data-id="element-681">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-682">
                          {merchant.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-683">
                          ID: {merchant.id} • {merchant.businessType}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-684">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${merchant.tier === 'Premium' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : merchant.tier === 'Standard' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`} data-id="element-685">
                      {merchant.tier}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-686">
                    <div className="flex items-center" data-id="element-687">
                      {merchant.portalAccess ? <StatusBadge status="success" text="Enabled" data-id="element-688" /> : <StatusBadge status="danger" text="Disabled" data-id="element-689" />}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-690">
                    {formatDate(merchant.lastLogin)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-691">
                    {merchant.portalAccess ? <button onClick={() => openFeaturesModal(merchant)} className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-692">
                        <SettingsIcon className="h-4 w-4 mr-1" data-id="element-693" />
                        Configure
                      </button> : <span className="text-gray-400 dark:text-gray-500" data-id="element-694">
                        Not available
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-695">
                    {merchant.portalAccess ? <button onClick={() => openRolesModal(merchant)} className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-696">
                        {merchant.roles.reduce((sum, role) => sum + role.count, 0)}{' '}
                        users
                      </button> : <span className="text-gray-400 dark:text-gray-500" data-id="element-697">
                        No users
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-698">
                    <button onClick={() => togglePortalAccess(merchant)} className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${merchant.portalAccess ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'}`} data-id="element-699">
                      {merchant.portalAccess ? <>
                          <UserXIcon className="h-3.5 w-3.5 mr-1" data-id="element-700" />
                          Disable Access
                        </> : <>
                          <UserCheckIcon className="h-3.5 w-3.5 mr-1" data-id="element-701" />
                          Enable Access
                        </>}
                    </button>
                  </td>
                </tr>)}
              {filteredMerchants.length === 0 && <tr data-id="element-702">
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-703">
                    No merchants found matching your filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Features Configuration Modal */}
      <Modal isOpen={showFeaturesModal} onClose={() => setShowFeaturesModal(false)} title="Configure Portal Features" size="lg" data-id="element-704">
        {selectedMerchant && <div className="space-y-6" data-id="element-705">
            <div className="flex items-center" data-id="element-706">
              <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30" data-id="element-707">
                <BuildingIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-708" />
              </div>
              <div className="ml-4" data-id="element-709">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-710">
                  {selectedMerchant.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-711">
                  {selectedMerchant.tier} Tier • ID: {selectedMerchant.id}
                </p>
              </div>
            </div>
            <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-3" data-id="element-712">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-713">
                Enable or disable features for this merchant
              </h4>
              <div className="flex space-x-2" data-id="element-714">
                <button onClick={() => {
              setBulkEnableDisable('enable');
              handleBulkFeatureChange();
            }} className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-md text-xs font-medium" data-id="element-715">
                  Enable All
                </button>
                <button onClick={() => {
              setBulkEnableDisable('disable');
              handleBulkFeatureChange();
            }} className="px-3 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded-md text-xs font-medium" data-id="element-716">
                  Disable All
                </button>
              </div>
            </div>
            <div className="space-y-4" data-id="element-717">
              {Object.entries(editedFeatures).map(([feature, enabled]) => <div key={feature} className="flex items-center justify-between" data-id="element-718">
                  <div data-id="element-719">
                    <div className="flex items-center" data-id="element-720">
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-721">
                        {feature.charAt(0).toUpperCase() + feature.slice(1).replace(/([A-Z])/g, ' $1')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-722">
                      {featureDescriptions[feature as keyof typeof featureDescriptions]}
                    </p>
                  </div>
                  <div className="flex items-center" data-id="element-723">
                    <button onClick={() => handleFeatureChange(feature, true)} className={`mr-2 px-3 py-1 rounded-md text-xs font-medium ${enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`} data-id="element-724">
                      Enable
                    </button>
                    <button onClick={() => handleFeatureChange(feature, false)} className={`px-3 py-1 rounded-md text-xs font-medium ${!enabled ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`} data-id="element-725">
                      Disable
                    </button>
                  </div>
                </div>)}
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-726">
              <button onClick={() => setShowFeaturesModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-727">
                Cancel
              </button>
              <button onClick={saveFeatures} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center" data-id="element-728">
                <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-729" />
                Save Changes
              </button>
            </div>
          </div>}
      </Modal>
      {/* Roles Modal */}
      <Modal isOpen={showRolesModal} onClose={() => setShowRolesModal(false)} title="Manage User Roles" size="lg" data-id="element-730">
        {selectedMerchant && <div className="space-y-6" data-id="element-731">
            <div className="flex items-center" data-id="element-732">
              <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30" data-id="element-733">
                <BuildingIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-734" />
              </div>
              <div className="ml-4" data-id="element-735">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-736">
                  {selectedMerchant.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-737">
                  {selectedMerchant.tier} Tier • ID: {selectedMerchant.id}
                </p>
              </div>
            </div>
            <div className="border-b border-gray-200 dark:border-gray-700 pb-3" data-id="element-738">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-739">
                User roles and permissions
              </h4>
            </div>
            <div className="space-y-6" data-id="element-740">
              {selectedMerchant.roles.length > 0 ? selectedMerchant.roles.map(role => <div key={role.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4" data-id="element-741">
                    <div className="flex items-center justify-between mb-3" data-id="element-742">
                      <div className="flex items-center" data-id="element-743">
                        <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30 mr-3" data-id="element-744">
                          <ShieldIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-745" />
                        </div>
                        <div data-id="element-746">
                          <h5 className="text-base font-medium text-gray-900 dark:text-white" data-id="element-747">
                            {role.name}
                          </h5>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-748">
                            {role.count} {role.count === 1 ? 'user' : 'users'}{' '}
                            with this role
                          </p>
                        </div>
                      </div>
                      <a href="#" className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300" data-id="element-749">
                        Manage Users
                      </a>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-750">
                      {rolePermissions[role.id as keyof typeof rolePermissions]?.description || 'Custom role'}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-751">
                      {rolePermissions[role.id as keyof typeof rolePermissions]?.permissions.map((permission, index) => <div key={index} className="flex items-center" data-id="element-752">
                          <CheckIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-753" />
                          <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-754">
                            {permission}
                          </span>
                        </div>)}
                    </div>
                  </div>) : <div className="text-center py-6 text-gray-500 dark:text-gray-400" data-id="element-755">
                  No user roles defined for this merchant.
                </div>}
              <div className="text-center" data-id="element-756">
                <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-757">
                  Add New Role
                </button>
              </div>
            </div>
            <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-758">
              <button onClick={() => setShowRolesModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-759">
                Close
              </button>
            </div>
          </div>}
      </Modal>
      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="Confirm Access Removal" data-id="element-760">
        {selectedMerchant && <div className="space-y-4" data-id="element-761">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4" data-id="element-762">
              <div className="flex" data-id="element-763">
                <div className="flex-shrink-0" data-id="element-764">
                  <AlertCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" data-id="element-765" />
                </div>
                <div className="ml-3" data-id="element-766">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-767">
                    Warning: This action will disable portal access
                  </h3>
                  <div className="mt-2 text-sm text-red-700 dark:text-red-200" data-id="element-768">
                    <p data-id="element-769">
                      You are about to disable portal access for{' '}
                      <strong data-id="element-770">{selectedMerchant.name}</strong>. This will:
                    </p>
                    <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-771">
                      <li data-id="element-772">Immediately revoke access for all users</li>
                      <li data-id="element-773">Disable all features and configurations</li>
                      <li data-id="element-774">Require reconfiguration if access is re-enabled</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-gray-700 dark:text-gray-300" data-id="element-775">
              Are you sure you want to continue?
            </p>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-776">
              <button onClick={() => setShowConfirmModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-777">
                Cancel
              </button>
              <button onClick={() => updateMerchantAccess(selectedMerchant, false)} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-778">
                Disable Access
              </button>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default MerchantAccessControl;